import React, { useState, useEffect } from 'react';
import { Plus, X, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Predefined keys for additional information
const PREDEFINED_KEYS = [
  { value: 'name_tamil', label: 'Name (Tamil)' },
] as const;

interface AdditionalFieldsProps {
  value?: Record<string, any> | null;
  onChange: (value: Record<string, any>) => void;
  disabled?: boolean;
}

export function AdditionalFields({ value, onChange, disabled = false }: AdditionalFieldsProps) {
  // State for managing key-value pairs
  const [fields, setFields] = useState<Array<{ key: string; value: string; id: string }>>(
    value ? Object.entries(value).map(([key, val], index) => ({ 
      key, 
      value: String(val),
      id: `field-${Date.now()}-${index}` // Unique ID for React keys
    })) : []
  );

  // Update parent whenever fields change
  useEffect(() => {
    const validFields = fields.filter(field => field.key.trim() !== '');
    const fieldsObject = validFields.reduce((acc, field) => {
      if (field.key.trim()) {
        acc[field.key] = field.value;
      }
      return acc;
    }, {} as Record<string, string>);
    
    onChange(Object.keys(fieldsObject).length > 0 ? fieldsObject : {});
  }, [fields]);

  const addField = () => {
    setFields([...fields, { 
      key: '', 
      value: '', 
      id: `field-${Date.now()}` 
    }]);
  };

  const updateField = (id: string, type: 'key' | 'value', newValue: string) => {
    setFields(fields.map(field => 
      field.id === id 
        ? { ...field, [type]: newValue }
        : field
    ));
  };

  const removeField = (id: string) => {
    setFields(fields.filter(field => field.id !== id));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Additional Information
        </CardTitle>
        <CardDescription>
          Add additional item information by selecting a key from predefined options and providing the value
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Info className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">No additional information added yet</p>
            <p className="text-xs mt-1">Click the button below to add custom fields</p>
          </div>
        ) : (
          <div className="space-y-3">
            {fields.map((field) => (
              <div key={field.id} className="flex gap-2 items-center">
                <Select
                  value={field.key}
                  onValueChange={(value) => updateField(field.id, 'key', value)}
                  disabled={disabled}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select key" />
                  </SelectTrigger>
                  <SelectContent>
                    {PREDEFINED_KEYS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  placeholder="Value"
                  value={field.value}
                  onChange={(e) => updateField(field.id, 'value', e.target.value)}
                  disabled={disabled}
                  className="flex-1"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeField(field.id)}
                  disabled={disabled}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addField}
          disabled={disabled}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Field
        </Button>
        
        {fields.length > 0 && (
          <div className="text-xs text-muted-foreground mt-2">
            These fields will be stored as additional information and can be used for various purposes
          </div>
        )}
      </CardContent>
    </Card>
  );
}